import { DashboardCard } from "@/components/design-system/dashboard-card";
import AppLayout from "@/components/layout/AppLayout";
import { selectUserRole } from "@/store/slices/authSlice";
import {
  fetchDashboardData,
  selectDashboardData,
  selectDashboardLoading,
} from "@/store/slices/dashboardSlice";
import { ROLES } from "@/utils/constants";
import { BookOpenCheck, School, GraduationCap } from "lucide-react";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@store";
import { useLocation, useNavigate } from "react-router-dom";
import StudentListSection from "./components/students-list-section";
import SchoolListSection from "./components/school-list-section";
import StudentCourseCompletedSection from "./components/student-course-completed";
import {
  selectTotalItems as selectSchoolTotalItems,
  selectQuery as selectSchoolQuery,
  selectSchoolLoading,
} from "@/store/slices/schoolSlice";
import {
  selectTotalItems as selectStudentTotalItems,
  selectQuery as selectStudentQuery,
  selectStudentLoading,
} from "@/store/slices/studentSlice";

function DashboardPage() {
  const userRole = useSelector(selectUserRole);
  const isTeacher = userRole?.code === ROLES.TEACHER;
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(selectDashboardData);
  const loading = useSelector(selectDashboardLoading);
  const location = useLocation();
  const navigate = useNavigate();

  // Get the selected card from URL query parameters or set default based on role
  const getInitialSelectedCard = () => {
    const params = new URLSearchParams(location.search);
    const cardFromUrl = params.get("card");
    return cardFromUrl || (isTeacher ? "students" : "schools");
  };

  const [selectedCard, setSelectedCard] = useState<string | null>(
    getInitialSelectedCard()
  );

  const schoolTotalItems = useSelector(selectSchoolTotalItems, shallowEqual);
  const studentTotalItems = useSelector(selectStudentTotalItems, shallowEqual);

  const schoolQuery = useSelector(selectSchoolQuery, shallowEqual);
  const studentQuery = useSelector(selectStudentQuery, shallowEqual);
  const studentLoading = useSelector(selectStudentLoading, shallowEqual);
  const schoolLoading = useSelector(selectSchoolLoading, shallowEqual);

  // Update URL when selected card changes
  const updateSelectedCard = (card: string) => {
    setSelectedCard(card);
    const params = new URLSearchParams(location.search);
    params.set("card", card);
    navigate({ search: params.toString() }, { replace: true });
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  const schoolCount =
    selectedCard === "schools" && schoolQuery.fromDate && schoolQuery.toDate
      ? schoolTotalItems
      : dashboardData?.total_schools;

  const studentCount =
    selectedCard === "students" &&
    (studentQuery.gradeId || (studentQuery.fromDate && studentQuery.toDate) || studentQuery.schoolId)
      ? studentTotalItems
      : dashboardData?.total_students;

  const studentCompletedCount =
    selectedCard === "students-completed" &&
    ((studentQuery.fromDate && studentQuery.toDate) || studentQuery.schoolId)
      ? studentTotalItems
      : dashboardData?.course_completed;
console.log(studentQuery, "studentCompletedCount");
  return (
    <AppLayout>
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-shrink-0 mb-[35px]">
          {/* School card - Only visible for non-teacher roles */}
          {!isTeacher && (
            <DashboardCard
              onClick={() => updateSelectedCard("schools")}
              title="Number of Schools On-Boarded"
              index={0}
              value={loading ? "" : schoolCount || "0"}
              icon={<School className="w-[35px] h-[35px]" />}
              waveColor="blue"
              isActive={selectedCard === "schools"}
              loading={selectedCard === "schools" && schoolLoading}
            />
          )}

          <DashboardCard
            onClick={() => updateSelectedCard("students")}
            title="Number of Students On-Boarded"
            index={1}
            value={loading ? "" : studentCount || "0"}
            icon={<GraduationCap className="w-[26px] h-[26px]" />}
            waveColor="blue"
            isActive={selectedCard === "students"}
            loading={selectedCard === "students" && studentLoading}
          />

          <DashboardCard
            onClick={() => updateSelectedCard("students-completed")}
            title="Number of Students Completed Course"
            index={2}
            value={loading ? "" : studentCompletedCount || "0"}
            icon={<BookOpenCheck className="w-[24px] h-[24px]" />}
            waveColor="blue"
            isActive={selectedCard === "students-completed"}
            loading={selectedCard === "students-completed" && studentLoading}
          />
        </div>

        {/* Students List - Takes remaining height */}
        <div className="min-h-[40rem]">
          {/* School section - Only visible for non-teacher roles */}
          {selectedCard === "schools" && !isTeacher && <SchoolListSection />}
          {selectedCard === "students" && <StudentListSection />}
          {selectedCard === "students-completed" && (
            <StudentCourseCompletedSection />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default DashboardPage;
